import React, { useState, useEffect, useRef, useCallback } from 'react';

export default function Preloader({ minimumTime = 1500, onComplete }) {
    const [phase, setPhase] = useState('loading'); // 'loading' | 'exiting' | 'done'
    const [progress, setProgress] = useState(0);
    const [skipped, setSkipped] = useState(false);
    const rafRef = useRef(null);
    const startTimeRef = useRef(Date.now());

    // Smooth progress counter
    useEffect(() => {
        const hasLoaded = sessionStorage.getItem('preloader_shown');
        if (hasLoaded) {
            setPhase('done');
            onComplete?.();
            return;
        }

        const duration = minimumTime;

        const tick = () => {
            const elapsed = Date.now() - startTimeRef.current;
            const raw = Math.min((elapsed / duration) * 100, 100);
            // Ease out cubic
            const eased = 100 - Math.pow(1 - raw / 100, 3) * 100;

            setProgress(Math.floor(eased));

            if (raw < 100) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                setPhase('exiting');
                sessionStorage.setItem('preloader_shown', 'true');
                // Allow exit animation to play
                setTimeout(() => {
                    setPhase('done');
                    onComplete?.();
                }, 800);
            }
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [minimumTime, onComplete]);

    const handleSkip = useCallback(() => {
        if (phase !== 'loading') return;
        setSkipped(true);
        cancelAnimationFrame(rafRef.current);
        setProgress(100);
        setPhase('exiting');
        sessionStorage.setItem('preloader_shown', 'true');
        setTimeout(() => {
            setPhase('done');
            onComplete?.();
        }, 800);
    }, [phase, onComplete]);

    if (phase === 'done') return null;

    const isExiting = phase === 'exiting';

    return (
        <div
            role="status"
            aria-live="polite"
            aria-busy={!isExiting}
            onClick={handleSkip}
            className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#030308] cursor-pointer select-none
                transition-all duration-[800ms] ease-[cubic-bezier(0.76,0,0.24,1)]
                ${isExiting ? 'opacity-0 scale-110 blur-sm pointer-events-none' : 'opacity-100 scale-100 blur-0'}
            `}
        >
            {/* Ambient noise texture */}
            <div
                className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                    backgroundSize: '128px'
                }}
            />

            {/* Slow rotating starfield */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(3)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.03]"
                        style={{
                            width: `${300 + i * 200}px`,
                            height: `${300 + i * 200}px`,
                            animation: `spin ${20 + i * 10}s linear infinite ${i % 2 === 0 ? '' : 'reverse'}`
                        }}
                    />
                ))}
                {/* Random stars */}
                {[...Array(20)].map((_, i) => (
                    <div
                        key={`star-${i}`}
                        className="absolute w-[1px] h-[1px] bg-white rounded-full animate-pulse"
                        style={{
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                            animationDuration: `${2 + Math.random() * 3}s`,
                            opacity: 0.2 + Math.random() * 0.5
                        }}
                    />
                ))}
            </div>

            {/* Galaxy Core */}
            <div className="relative flex flex-col items-center">
                <div className="relative w-40 h-40 md:w-48 md:h-48">
                    {/* Outer glow */}
                    <div className="absolute inset-0 rounded-full bg-amber-500/10 blur-3xl animate-pulse" />

                    {/* Orbital rings */}
                    <svg className="w-full h-full animate-[spin_12s_linear_infinite]" viewBox="0 0 200 200">
                        <defs>
                            <linearGradient id="ring1" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0" />
                                <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.6" />
                                <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
                            </linearGradient>
                            <linearGradient id="ring2" x1="100%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
                                <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.6" />
                                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                            </linearGradient>
                        </defs>

                        {/* Elliptical orbit 1 */}
                        <ellipse
                            cx="100" cy="100" rx="90" ry="30"
                            fill="none" stroke="url(#ring1)" strokeWidth="0.5"
                            transform="rotate(30 100 100)"
                            strokeDasharray="4 4"
                        />
                        {/* Elliptical orbit 2 */}
                        <ellipse
                            cx="100" cy="100" rx="85" ry="35"
                            fill="none" stroke="url(#ring2)" strokeWidth="0.5"
                            transform="rotate(-20 100 100)"
                            strokeDasharray="2 6"
                        />
                        {/* Small orbiting dots */}
                        <circle r="2" fill="#fbbf24">
                            <animateMotion dur="4s" repeatCount="indefinite"
                                path="M 100 100 m -90 0 a 90 30 30 1 1 180 0 a 90 30 30 1 1 -180 0" />
                        </circle>
                        <circle r="1.5" fill="#60a5fa">
                            <animateMotion dur="5s" repeatCount="indefinite" begin="1s"
                                path="M 100 100 m -85 0 a 85 35 -20 1 1 170 0 a 85 35 -20 1 1 -170 0" />
                        </circle>
                    </svg>

                    {/* Counter-rotating inner ring */}
                    <svg className="absolute inset-0 w-full h-full animate-[spin_8s_linear_infinite_reverse]" viewBox="0 0 200 200">
                        <circle cx="100" cy="100" r="50" fill="none" stroke="#f59e0b" strokeWidth="0.3" opacity="0.3" strokeDasharray="1 8" />
                    </svg>

                    {/* Core sun */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 rounded-full bg-white shadow-[0_0_20px_#f59e0b,0_0_60px_#f59e0b44] animate-pulse" />
                        <div className="absolute inset-0 w-4 h-4 rounded-full bg-amber-400 blur-md animate-ping opacity-30" />
                    </div>
                </div>

                {/* Brand Text */}
                <div className="mt-8 text-center space-y-1 overflow-hidden">
                    <h1 className="text-3xl md:text-4xl font-black tracking-[0.2em] text-white uppercase font-heading">
                        {'Milestone'.split('').map((char, i) => (
                            <span
                                key={i}
                                className="inline-block animate-[fadeInUp_0.5s_ease-out_forwards]"
                                style={{
                                    animationDelay: `${0.3 + i * 0.05}s`,
                                    opacity: 0
                                }}
                            >
                                {char}
                            </span>
                        ))}
                    </h1>
                    <p className="text-xs tracking-[0.5em] text-amber-500 font-bold uppercase font-heading animate-[fadeIn_1s_ease-out_0.8s_forwards]" style={{ opacity: 0 }}>
                        Delight Galaxy
                    </p>
                </div>

                {/* Progress */}
                <div className="mt-8 w-48 space-y-2">
                    <div className="flex justify-between text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                        <span className="flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-blue-500 animate-pulse" />
                            {skipped ? 'Skipped' : 'Initializing'}
                        </span>
                        <span className="text-amber-500">{progress}%</span>
                    </div>
                    <div className="h-[2px] bg-slate-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-amber-500 to-blue-500 rounded-full transition-all duration-100 ease-out"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                {/* Skip hint */}
                <div className={`mt-6 text-[10px] text-slate-600 tracking-widest uppercase transition-opacity duration-500 ${progress > 30 ? 'opacity-100' : 'opacity-0'}`}>
                    Click anywhere to skip
                </div>
            </div>

            {/* Keyframe styles */}
            <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes spin {
                    from { transform: translate(-50%, -50%) rotate(0deg); }
                    to { transform: translate(-50%, -50%) rotate(360deg); }
                }
            `}</style>
        </div>
    );
}